/**
 * Structured-data builders for NetoVibe.
 * All values are safe to serialize to a <script type="application/ld+json"> tag.
 */

const BRAND = {
  name: "NetoVibe",
  legalName: "NetoVibe Web Design Studio",
  logo: "/brand/netovibe-logo.png",
  mark: "/brand/netovibe-mark.png",
  email: "netovibe@outlook.com",
  telephone: "+49-155-6944-5772",
  whatsapp: "+49-155-6327-1439",
  addressLocality: "Berlin",
  addressCountry: "DE",
  areaServed: ["DE", "AT", "CH", "US", "GB", "EU"],
  languages: ["en", "de"],
  priceRange: "€€",
  slogan: "Freelance craft. Agency scale.",
};

const abs = (path) => {
  if (typeof window === "undefined") return path;
  if (/^https?:/i.test(path)) return path;
  return `${window.location.origin}${path.startsWith("/") ? "" : "/"}${path}`;
};

export const organizationLd = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${abs("/")}#organization`,
  name: BRAND.name,
  legalName: BRAND.legalName,
  url: abs("/"),
  logo: abs(BRAND.logo),
  image: abs(BRAND.logo),
  slogan: BRAND.slogan,
  email: BRAND.email,
  telephone: BRAND.telephone,
  address: {
    "@type": "PostalAddress",
    addressLocality: BRAND.addressLocality,
    addressCountry: BRAND.addressCountry,
  },
  areaServed: BRAND.areaServed,
  contactPoint: [
    {
      "@type": "ContactPoint",
      email: BRAND.email,
      telephone: BRAND.telephone,
      contactType: "sales",
      areaServed: BRAND.areaServed,
      availableLanguage: BRAND.languages,
    },
    {
      "@type": "ContactPoint",
      telephone: BRAND.whatsapp,
      contactType: "customer support",
      contactOption: "TollFree",
      availableLanguage: BRAND.languages,
    },
  ],
});

export const websiteLd = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${abs("/")}#website`,
  name: BRAND.name,
  url: abs("/"),
  inLanguage: ["en-US", "de-DE"],
  publisher: { "@id": `${abs("/")}#organization` },
});

export const professionalServiceLd = () => ({
  "@context": "https://schema.org",
  "@type": ["ProfessionalService", "LocalBusiness"],
  "@id": `${abs("/")}#business`,
  name: `${BRAND.name} — Web Design Agency`,
  image: abs(BRAND.logo),
  url: abs("/"),
  telephone: BRAND.telephone,
  email: BRAND.email,
  priceRange: BRAND.priceRange,
  address: {
    "@type": "PostalAddress",
    addressLocality: BRAND.addressLocality,
    addressCountry: BRAND.addressCountry,
  },
  areaServed: BRAND.areaServed,
  serviceType: [
    "Web Design",
    "Custom Website Development",
    "Landing Page Design",
    "Bilingual Website Development",
    "SEO for Small Business",
    "Website Erstellung",
    "Homepage Erstellung",
    "Webdesign für Firmen",
  ],
  makesOffer: [
    {
      "@type": "Offer",
      name: "Basic Launch Pad",
      priceCurrency: "EUR",
      price: 600,
    },
    {
      "@type": "Offer",
      name: "Professional Business",
      priceCurrency: "EUR",
      price: 1000,
    },
    {
      "@type": "Offer",
      name: "Corporate Premium",
      priceCurrency: "EUR",
      price: 1500,
    },
  ],
});

export const breadcrumbLd = (crumbs) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: crumbs.map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: c.name,
    item: abs(c.path),
  })),
});

export const webPageLd = ({
  path,
  name,
  description,
  lang,
  type = "WebPage",
}) => ({
  "@context": "https://schema.org",
  "@type": type,
  "@id": `${abs(path)}#webpage`,
  url: abs(path),
  name,
  description,
  inLanguage: lang === "de" ? "de-DE" : "en-US",
  isPartOf: { "@id": `${abs("/")}#website` },
  about: { "@id": `${abs("/")}#organization` },
  primaryImageOfPage: abs(BRAND.logo),
});

export const faqPageLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((it) => ({
    "@type": "Question",
    name: it.q,
    acceptedAnswer: { "@type": "Answer", text: it.a },
  })),
});

export const servicePackageLd = ({ name, description, price, currency = "EUR" }) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Web Design",
  provider: { "@id": `${abs("/")}#organization` },
  name,
  description,
  areaServed: BRAND.areaServed,
  offers: {
    "@type": "Offer",
    price,
    priceCurrency: currency,
    availability: "https://schema.org/InStock",
    url: abs("/pricing"),
  },
});

export const caseStudyLd = ({ path, headline, description, lang }) => ({
  "@context": "https://schema.org",
  "@type": "CreativeWork",
  "@id": `${abs(path)}#casestudy`,
  headline,
  description,
  inLanguage: lang === "de" ? "de-DE" : "en-US",
  author: { "@id": `${abs("/")}#organization` },
  about: {
    "@type": "Organization",
    name: "Zemka Gebäudereinigung & Hausmeister Service",
    url: "https://zemka.de",
  },
});

export const reviewLd = () => ({
  "@context": "https://schema.org",
  "@type": "Review",
  "itemReviewed": {
    "@type": "ProfessionalService",
    "@id": `${abs("/")}#business`,
    "name": `${BRAND.name} — Web Design Agency`,
    "url": abs("/"),
  },
  "reviewRating": {
    "@type": "Rating",
    "ratingValue": "5",
    "bestRating": "5",
    "worstRating": "1",
  },
  "author": {
    "@type": "Organization",
    "name": "Zemka Gebäudereinigung & Hausmeister Service",
    "url": "https://zemka.de",
  },
  "reviewBody":
    "Honestly, before NetoVibe we didn't even have a website — everything ran on phone calls and word of mouth. NetoVibe made the whole process simple, no jargon, and when we went live my phone actually started buzzing with new enquiries the same week. Everything runs smoothly and we finally look as professional online as we are on site.",
});

export const howToLd = ({ name, steps, lang }) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  name,
  inLanguage: lang === "de" ? "de-DE" : "en-US",
  step: steps.map((s, i) => ({
    "@type": "HowToStep",
    position: i + 1,
    name: s.name,
    text: s.text,
  })),
});

export { BRAND };
